import { type SelectProps } from 'antd';
import { useMemo } from 'react';
import { type LanguagePackOptionDataFragment } from '../../api/fragments/LanguagePackOptionData';
import { useGetLanguagePackOptionsQuery } from '../../api/queries/getLanguagePackOptions';
import { useCompany } from '../contexts/CompanyContextManager';
import SelectField, { type SelectFieldProps } from './SelectField';

export type LanguageSelectFieldProps = Omit<SelectFieldProps, 'children'> & {
    languages?: LanguagePackOptionDataFragment[];
    shouldFilterByCompany?: boolean;
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.label.toString().toLowerCase().includes(inputValue.toLowerCase());

const LanguageSelectField = ({
    languages: languagesFromProps,
    shouldFilterByCompany = true,
    ...props
}: LanguageSelectFieldProps) => {
    const company = useCompany(true);
    const { data, loading } = useGetLanguagePackOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: shouldFilterByCompany ? { companyId: company?.id } : undefined },
        skip: !!languagesFromProps,
    });

    const languages = useMemo(
        () => languagesFromProps || data?.languages?.items,
        [data?.languages?.items, languagesFromProps]
    );

    const options = useMemo(
        () => (languages || []).map(language => ({ value: language.id, label: language.referenceName })),
        [languages]
    );

    return <SelectField {...props} filterOption={filterOption} loading={!languages && loading} options={options} />;
};

export default LanguageSelectField;
